import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { Select, SelectProps } from '@components/Select/Select';
import React, { FormEvent, useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  StyledInput,
  StyledSubmitButton,
  StyledWrapper,
  StyledEventTypes,
  StyledEventTypesMobile,
  StyledJoykitIcon,
  StyledJoykitIconMobile,
  FilterCta,
  ErrorDetail,
} from './SearchField.styles';

export interface SearchFieldProps<T> extends Omit<SelectProps<T>, 'onChange'> {
  page: MarcomPageName;
  inputError: string;
  placeholder: string;
  shouldAutoFocus: boolean;
  eventTypeStyles: {
    height: number;
    minWidth: number;
  };
  onChangeSelect: (eventType: T | '') => void;
  onChangeInput: (value: string) => void;
  onSubmit: () => void;
}

const SearchField = <T extends string>({
  page,
  inputError,
  placeholder,
  shouldAutoFocus,
  eventTypeStyles,
  onSubmit,
  onChangeInput,
  onChangeSelect,
  ...selectProps
}: SearchFieldProps<T>) => {
  const { selectAllLabel, labelFunction, options, value } = selectProps;

  const [inputValue, setInputValue] = useState<string | string[]>('');
  const isMobileMediaQuery = useThemeMediaQuery('below_sm');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleOnChangeSelect = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>, option: T | '') => {
      e.preventDefault();
      onChangeSelect(option);
    },
    [onChangeSelect]
  );

  const handleChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      onChangeInput(e.target.value);
    },
    [onChangeInput]
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit();
    },
    [onSubmit]
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit();
    }
  };

  useLayoutEffect(() => {
    const inputEl = inputRef.current;
    if (shouldAutoFocus && inputEl && !isMobileMediaQuery) {
      inputEl.focus();
    }
  }, [isMobileMediaQuery, shouldAutoFocus]);

  return (
    <form onSubmit={handleSubmit}>
      <StyledEventTypesMobile alignItems="center" justifyContent="center">
        <FilterCta
          id={`${page}FilterSelectAll`}
          variant="defaultPrimary"
          active={'' === value}
          onClick={(e) => handleOnChangeSelect(e, '')}
        >
          {selectAllLabel}
        </FilterCta>
        {options.map((option, idx) => (
          <FilterCta
            key={`${option}${idx}`}
            id={`${page}${option}FilterSelect`}
            active={option === value}
            onClick={(e) => handleOnChangeSelect(e, option)}
          >
            {labelFunction(option)}
          </FilterCta>
        ))}
      </StyledEventTypesMobile>
      <StyledWrapper padding={[2, 3]} paddingLeft={[5, 8]} alignItems="center">
        <StyledEventTypes alignItems="center" justifyContent="flex-end" paddingRight={6} {...eventTypeStyles}>
          <Select {...selectProps} onChange={(e) => onChangeSelect(e.target.value as unknown as T)} />
        </StyledEventTypes>
        <StyledInput
          ref={inputRef}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={handleChangeInput}
          onKeyDown={handleKeyPress}
          value={inputValue}
          placeholder={placeholder}
          required
        />
        <StyledSubmitButton id="submitSearchField" skipRedirectWithTelemetry>
          <StyledJoykitIconMobile name="search" color="white" size={32} />
          <StyledJoykitIcon name="searchLg" color="white" size={48} />
        </StyledSubmitButton>
      </StyledWrapper>
      {inputError && (
        <ErrorDetail tagName="p" variant="navParent15" color="red1">
          {inputError}
        </ErrorDetail>
      )}
    </form>
  );
};

SearchField.defaultProps = {
  page: 'find',
  placeholder: 'Type to search...',
  selectAllLabel: 'All',
  shouldAutoFocus: true,
  eventTypeStyles: {
    height: 80,
    minWidth: 160,
  },
};

SearchField.displayName = 'SearchField';

export { SearchField };
